import { useEffect, useState, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import { caseAtom, applicationTypeAtom } from 'atoms'
import { M3 } from '@messenger/consts'
import { validateEditToken } from '@/api/orders'
import { clearOrderSession, orderSessionId } from '@/utils/global/orderSession'

/**
 * Hook to validate messenger edit token for external editor access
 *
 * Only validates when shouldValidate is true (i.e., when access=fh_admin in URL)
 *
 * Checks:
 * 1. an edit-link order is held for this tab or, failing that, in the
 *    shared key (`orderSessionId`)
 * 2. tokenValidated flag is set in sessionStorage
 * 3. tokenValidatedAt is within 24 hours
 *
 * @param {boolean} shouldValidate - Whether to perform validation (true when access=fh_admin)
 * @returns {Object} { isValidSession, returnUrl, shouldCloseTab }
 */
export const useTokenValidation = (shouldValidate = false) => {
  const caseInfo = useRecoilValue(caseAtom)
  const applicationType = useRecoilValue(applicationTypeAtom)
  const [isValidSession, setIsValidSession] = useState(null)
  const [fetchedReturnUrl, setFetchedReturnUrl] = useState(null)

  // Check if this is M3 from URL params (for immediate detection before atom is set)
  const isM3 = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.has('m3OrderProductId') || applicationType === M3
  }, [applicationType])

  useEffect(() => {
    // If not validating (regular Auth0 users), allow access
    if (!shouldValidate) {
      setIsValidSession(true)
      return
    }

    const checkValidation = async () => {
      // Check if user went through proper token validation
      const messengerOrderIdToken = orderSessionId()
      const tokenValidated = sessionStorage.getItem('tokenValidated')
      const tokenValidatedAt = sessionStorage.getItem('tokenValidatedAt')

      if (
        messengerOrderIdToken &&
        tokenValidated === 'true' &&
        tokenValidatedAt
      ) {
        // Check if validation is recent (within last 24 hours)
        const validatedTime = parseInt(tokenValidatedAt, 10)
        const currentTime = Date.now()
        const twentyFourHours = 24 * 60 * 60 * 1000

        if (currentTime - validatedTime < twentyFourHours) {
          setIsValidSession(true)
          return
        } else {
          // Validation expired, clear session
          sessionStorage.removeItem('tokenValidated')
          sessionStorage.removeItem('tokenValidatedAt')
          clearOrderSession()
        }
      } else {
        // No valid session, user trying to bypass
        if (messengerOrderIdToken) {
          // Clear the order this session claimed without a validation
          clearOrderSession()
        }
      }

      // Validation failed - try to fetch returnUrl from server
      // Extract token from URL to get returnUrl from validateEditToken API
      const urlParams = new URLSearchParams(window.location.search)
      const editToken = urlParams.get('token')

      if (editToken && !isM3) {
        try {
          const tokenResponse = await validateEditToken(editToken)
          // Even if token is invalid, try to get returnUrl from response
          const returnUrlFromServer = tokenResponse.data.returnUrl
          if (returnUrlFromServer) {
            setFetchedReturnUrl(returnUrlFromServer)
          }
        } catch (error) {
          // Try to get returnUrl from error response
          const returnUrlFromError = error?.response?.data?.returnUrl
          if (returnUrlFromError) {
            setFetchedReturnUrl(returnUrlFromError)
          }
        }
      }

      setIsValidSession(false)
    }

    checkValidation()
  }, [shouldValidate, isM3])

  // For M3: close tab (no return URL)
  // For MDL: use fetched returnUrl, then case.returnUrl, then fallback to '/'
  const returnUrl = isM3 ? null : fetchedReturnUrl || caseInfo?.returnUrl || '/'
  const shouldCloseTab = isM3

  return { isValidSession, returnUrl, shouldCloseTab }
}
